import { Box, Table as MantineTable } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import classes from "./ListTable.module.css";
import {
  ListTableColumnFilter,
  ListTableColumnSorter,
  ListTableColumnsOrganizer,
  ListTableContext,
  ListTablePageSizeChanger,
  ListTablePagination,
  ListTableSearchInput,
  ListTableSelectionContext,
} from "./components";
import { renderListTableHead } from "./utils";

import { ChoosePlanModal } from "@/components/widgets/PlanModals";

import type { MantineSize, MantineStyleProps, TableProps } from "@mantine/core";
import type { FC, ReactNode } from "react";
import type { CheckStatus } from "./components";
import type { ListTableHead } from "./types";

export type ListTableData = {
  head?: ListTableHead;
  body: ReactNode[];
  foot?: ListTableHead;
  caption?: string;
};

export type ListTableComparisonProps = {
  checkStatus: CheckStatus;
  onToggleAll: () => void;
  withSelection?: boolean;
};

export type ListTableNoComparisonProps = {
  checkStatus?: never;
  onToggleAll?: never;
  withSelection?: never;
};

export type ListTableProps = {
  data: ListTableData;
  minWidth?: number;
  tableProps?: Pick<
    TableProps,
    | "borderColor"
    | "captionSide"
    | "highlightOnHover"
    | "highlightOnHoverColor"
    | "horizontalSpacing"
    | "stickyHeader"
    | "stickyHeaderOffset"
    | "striped"
    | "stripedColor"
    | "verticalSpacing"
  >;
  toggleProps?: {
    disabled?: boolean;
    size?: MantineSize;
    status?: "indeterminate" | "unchecked" | "checked";
  };
} & (ListTableComparisonProps | ListTableNoComparisonProps) &
  MantineStyleProps;

type TableComponent = FC<ListTableProps> & {
  ColumnFilter: typeof ListTableColumnFilter;
  ColumnSorter: typeof ListTableColumnSorter;
  ColumnsOrganizer: typeof ListTableColumnsOrganizer;
  Context: typeof ListTableContext;
  PageSizeChanger: typeof ListTablePageSizeChanger;
  Pagination: typeof ListTablePagination;
  SearchInput: typeof ListTableSearchInput;
  SelectionContext: typeof ListTableSelectionContext;
};

const Table: TableComponent = ({
  checkStatus,
  data: { head, body, foot, caption },
  minWidth = 500,
  onToggleAll,
  tableProps,
  withSelection = true,
  ...props
}) => {
  const { t } = useTranslation();
  const [showPlanModal, { open: openPlanModal, close: closePlanModal }] = useDisclosure();

  const selectionProps =
    !!checkStatus && !!onToggleAll ? { checkStatus, onToggleAll, withSelection } : undefined;

  // Avoid forwarding internal props (like `hasComparison`) to DOM elements
  // which would cause React warnings. Strip known non-DOM props before spreading.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { hasComparison, ...boxProps } = props as any;

  return (
    <Box {...boxProps}>
      {/** @ts-ignore - Props missing in TableScrollContainerProps, opened issue here: https://github.com/mantinedev/mantine/issues/7798 -- Still not fixed */}
      <MantineTable.ScrollContainer minWidth={minWidth} offsetScrollbars={false}>
        <MantineTable withRowBorders={false} className={classes.noRowBorder} {...tableProps}>
          {caption && <MantineTable.Caption>{caption}</MantineTable.Caption>}
          {head && (
            <MantineTable.Thead>
              {renderListTableHead(
                head,
                selectionProps,
                openPlanModal,
                t("plans.contentLocker.upgradeToAccess")
              )}
            </MantineTable.Thead>
          )}
          <MantineTable.Tbody>{body}</MantineTable.Tbody>
          {foot && (
            <MantineTable.Tfoot>
              {renderListTableHead(
                foot,
                selectionProps,
                openPlanModal,
                t("plans.contentLocker.upgradeToAccess")
              )}
            </MantineTable.Tfoot>
          )}
        </MantineTable>
      </MantineTable.ScrollContainer>
      <ChoosePlanModal
        isOpen={showPlanModal}
        onClose={() => closePlanModal()}
        title={t("plans.changePlanAnytime")}
      />
    </Box>
  );
};

Table.ColumnFilter = ListTableColumnFilter;
Table.ColumnSorter = ListTableColumnSorter;
Table.ColumnsOrganizer = ListTableColumnsOrganizer;
Table.Context = ListTableContext;
Table.PageSizeChanger = ListTablePageSizeChanger;
Table.Pagination = ListTablePagination;
Table.SearchInput = ListTableSearchInput;
Table.SelectionContext = ListTableSelectionContext;

export const ListTable = Table;
